// /settings/security — TOTP status + regenerate recovery codes.
//
// Talks to admin-api (admin_session cookie) — not the main HCM API bearer.
// The admin-api endpoints live at /admin/mfa/* and operate on the same shared
// `users` collection, so behavior matches apps/api /v1/mfa exactly. Routing
// here removes the bearer-token dependency that caused "Could not load TOTP
// status" when the main-API session expired or was missing from this tab's
// sessionStorage.

import { useEffect, useState } from "react";
import type { JSX } from "react";
import { Link } from "react-router-dom";
import { AdminNav } from "../components/AdminNav.js";
import { adminFetch } from "../lib/api.js";

interface MfaStatus {
  enabled: boolean;
  recovery_codes_count: number;
}

interface RegenerateResponse {
  recovery_codes: string[];
  message: string;
}

function buildRecoveryFile(codes: string[]): string {
  return [
    "HCM Admin Recovery Codes",
    "========================",
    `Generated: ${new Date().toISOString()}`,
    "",
    "Use these codes if you lose access to Google Authenticator.",
    "Each code can only be used ONCE.",
    "",
    ...codes.map((c, i) => `${i + 1}. ${c}`),
    "",
    "Keep this file safe and offline.",
  ].join("\n");
}

// eslint-disable-next-line max-lines-per-function, complexity -- multi-section settings page (MFA, recovery, sessions)
export function SecuritySettingsPage(): JSX.Element {
  const [status, setStatus] = useState<MfaStatus | null>(null);
  const [error, setError]   = useState<string | null>(null);

  const [showRegen, setShowRegen] = useState(false);
  const [totpCode,  setTotpCode]  = useState("");
  const [regenSubmitting, setRegenSubmitting] = useState(false);
  const [newCodes,  setNewCodes]  = useState<string[] | null>(null);
  const [copied,    setCopied]    = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        const data = await adminFetch<MfaStatus>("/admin/mfa/status");
        setStatus(data);
      } catch {
        setError("Could not load TOTP status. Try signing out and back in.");
      }
    })();
  }, []);

  async function handleRegenerate(): Promise<void> {
    if (totpCode.length !== 6) { setError("Enter the current 6-digit code from your authenticator app."); return; }
    setError(null);
    setRegenSubmitting(true);
    try {
      const res = await adminFetch<RegenerateResponse>("/admin/mfa/recovery-codes/regenerate", {
        method: "POST",
        body:   JSON.stringify({ token: totpCode }),
      });
      setNewCodes(res.recovery_codes);
      setStatus((s) => s ? { ...s, recovery_codes_count: res.recovery_codes.length } : s);
    } catch (err) {
      setError(err instanceof Error && err.message.includes("400")
        ? "Invalid code — try again."
        : "Could not regenerate codes.");
    } finally {
      setRegenSubmitting(false);
    }
  }

  function copyCodes(): void {
    if (!newCodes) return;
    navigator.clipboard.writeText(newCodes.join("\n"))
      .then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); })
      .catch(() => { /* clipboard unavailable */ });
  }

  function downloadCodes(): void {
    if (!newCodes) return;
    const blob = new Blob([buildRecoveryFile(newCodes)], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "hcm-admin-recovery-codes.txt"; a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="min-h-screen bg-slate-100">
      <AdminNav />
      <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        <h1 className="text-xl font-semibold text-slate-900">Security</h1>

        <section className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 space-y-4">
          <div>
            <h2 className="text-base font-semibold text-slate-900">Two-factor authentication</h2>
            <p className="text-sm text-slate-500 mt-1">
              Required for super-admin accounts. Uses Google Authenticator (TOTP).
            </p>
          </div>
          {error && <p role="alert" className="text-sm text-red-600">{error}</p>}
          {status && (
            <dl className="grid grid-cols-2 gap-3 text-sm">
              <dt className="text-slate-500">Status</dt>
              <dd className={status.enabled ? "text-emerald-700 font-medium" : "text-amber-700 font-medium"}>
                {status.enabled ? "Enabled" : "Not set up"}
              </dd>
              <dt className="text-slate-500">Recovery codes remaining</dt>
              <dd className="text-slate-900 font-mono">{status.recovery_codes_count}</dd>
            </dl>
          )}
          {status && !status.enabled && (
            <Link to="/setup-totp"
              className="inline-block py-2 px-4 rounded-lg bg-indigo-700 text-white font-medium text-sm hover:bg-indigo-800">
              Set up two-factor authentication
            </Link>
          )}
        </section>

        {status?.enabled && (
          <section className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 space-y-4">
            <div>
              <h2 className="text-base font-semibold text-slate-900">Recovery codes</h2>
              <p className="text-sm text-slate-500 mt-1">
                Regenerating invalidates all previous codes. Keep at least one new code in a password manager.
              </p>
            </div>

            {newCodes ? (
              <>
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                  <div className="grid grid-cols-2 gap-2">
                    {newCodes.map((code) => (
                      <code key={code} className="text-sm font-mono text-slate-700 bg-white border border-slate-200 rounded px-2 py-1 text-center">
                        {code}
                      </code>
                    ))}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={copyCodes}
                    className="flex-1 py-1.5 px-3 rounded-lg border border-slate-300 text-sm text-slate-700 hover:bg-slate-50">
                    {copied ? "✅ Copied!" : "Copy codes"}
                  </button>
                  <button onClick={downloadCodes}
                    className="flex-1 py-1.5 px-3 rounded-lg border border-slate-300 text-sm text-slate-700 hover:bg-slate-50">
                    ⬇ Download
                  </button>
                </div>
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                  Save these now. They will not be shown again.
                </p>
              </>
            ) : showRegen ? (
              <div className="space-y-3">
                <p className="text-sm text-slate-600">Enter the 6-digit code from your authenticator app to confirm.</p>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ""))}
                  placeholder="000000"
                  className="w-full max-w-xs text-center text-xl font-mono tracking-widest rounded-lg border border-slate-300 px-3 py-3 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
                <div className="flex gap-2">
                  <button
                    onClick={() => { setShowRegen(false); setTotpCode(""); setError(null); }}
                    disabled={regenSubmitting}
                    className="py-2 px-4 rounded-lg border border-slate-300 text-sm text-slate-700 hover:bg-slate-50">
                    Cancel
                  </button>
                  <button
                    onClick={() => { void handleRegenerate(); }}
                    disabled={totpCode.length !== 6 || regenSubmitting}
                    className="py-2 px-4 rounded-lg bg-indigo-700 text-white text-sm font-medium hover:bg-indigo-800 disabled:opacity-50">
                    {regenSubmitting ? "Regenerating…" : "Regenerate codes"}
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => { setShowRegen(true); setError(null); }}
                className="py-2 px-4 rounded-lg border border-slate-300 text-sm text-slate-700 hover:bg-slate-50">
                Regenerate recovery codes
              </button>
            )}
          </section>
        )}
      </div>
    </div>
  );
}
